import { useEffect, useState } from "react";
import { imgUrl, fmtClock } from "../lib/api";

export const getPrefs = () => ({ brightness: 85, duration: 5, flashing: true, reducedMotion: false, ...JSON.parse(localStorage.getItem("tro_device_prefs") || "{}") });
export const savePrefs = (p) => localStorage.setItem("tro_device_prefs", JSON.stringify(p));

const COLORS = { yellow: "#FFD400", red: "#F02030" };

export function CardAlert({ alert, onClose }) {
  const prefs = getPrefs();
  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(onClose, prefs.duration * 1000);
    return () => clearTimeout(t);
  }, [alert, onClose, prefs.duration]);
  if (!alert) return null;
  const c = COLORS[alert.color];
  const flash = prefs.flashing && !prefs.reducedMotion;
  return (
    <div data-testid={`card-alert-${alert.color}`} onClick={onClose}
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center p-6 text-center ${flash ? "alert-flash" : ""}`} style={{ background: c }}>
      <div className={`mb-6 h-40 w-28 rounded-xl border-4 shadow-2xl ${alert.color === "yellow" ? "border-[#17191C]/20 bg-[#FFE45C]" : "border-white/30 bg-[#ff4f5c]"}`} />
      <div className={`text-5xl font-extrabold uppercase tracking-tight md:text-7xl ${alert.color === "yellow" ? "text-[#17191C]" : "text-white"}`}>
        {alert.event_type === "second_yellow" ? "Second Caution · Red Card" : alert.color === "yellow" ? "Yellow Card" : "Red Card"}
      </div>
      {alert.player && (
        <div className={`mt-6 flex items-center gap-4 rounded-2xl px-5 py-3 ${alert.color === "yellow" ? "bg-[#17191C] text-white" : "bg-white text-[#17191C]"}`}>
          <div className="h-16 w-16 overflow-hidden rounded-full bg-[#E6E9ED]">{imgUrl(alert.player.photo) && <img src={imgUrl(alert.player.photo)} alt="" className="h-full w-full object-cover" />}</div>
          <div className="text-left">
            <div className="text-2xl font-extrabold">#{alert.player.number} {alert.player.name}</div>
            <div className="text-sm font-semibold opacity-80">{alert.player.club} · {fmtClock((alert.minute || 0) * 60 + (alert.second || 0))}</div>
          </div>
        </div>
      )}
      <div className={`mt-8 text-xs font-bold uppercase tracking-widest ${alert.color === "yellow" ? "text-[#17191C]/60" : "text-white/70"}`}>Tap to return to tactical screen</div>
    </div>
  );
}

function LedMatrix({ color, brightness, flash }) {
  const cols = 14, rows = 26;
  return (
    <div className="grid h-full w-full gap-[3px] p-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {Array.from({ length: cols * rows }).map((_, i) => {
        const on = !!color;
        const geo = ((i % cols) + Math.floor(i / cols)) % 3 === 0;
        return (
          <span key={i} className={`led-dot aspect-square ${on && flash ? "led-flash" : ""}`}
            style={{ background: on ? COLORS[color] : geo ? "#4a5056" : "#3a3f45", opacity: on ? brightness / 100 : 0.9,
              boxShadow: on ? `0 0 6px ${COLORS[color]}` : "none", animationDelay: on && flash ? `${(i % cols) * 25}ms` : undefined }} />
        );
      })}
    </div>
  );
}

export function DeviceSimulator({ mode = "normal", alert, frontNormal, small, prefs: pp }) {
  const prefs = pp || getPrefs();
  const color = mode === "normal" ? null : mode;
  const flash = prefs.flashing && !prefs.reducedMotion;
  const w = small ? 150 : 230;
  const [side, setSide] = useState("both");
  const Body = ({ children, testid }) => (
    <div data-testid={testid} className="relative tro-metal-dark p-[7px] shadow-xl" style={{ width: w, height: w * 2.05, borderRadius: small ? 26 : 36 }}>
      <span className="absolute -right-[3px] top-[22%] h-10 w-[3px] rounded-r bg-[#8d959d]" />
      <span className="absolute -right-[3px] top-[36%] h-16 w-[3px] rounded-r bg-[#8d959d]" />
      <span className="absolute -left-[3px] top-[28%] h-12 w-[3px] rounded-l bg-[#F02030]/70" title="Quick card button" />
      <div className="h-full w-full overflow-hidden bg-[#17191C]" style={{ borderRadius: small ? 20 : 30 }}>{children}</div>
    </div>
  );
  return (
    <div data-testid="device-simulator" className="flex flex-col items-center gap-3">
      {!small && (
        <div className="flex gap-1">{["front", "both", "rear"].map((s) => (
          <button key={s} data-testid={`device-view-${s}`} onClick={() => setSide(s)} className={`rounded-full px-3 py-1 text-xs font-bold ${side === s ? "bg-[#17191C] text-white" : "bg-[#F7F8F9]"}`}>{s}</button>
        ))}</div>
      )}
      <div className="flex flex-wrap items-start justify-center gap-5">
        {side !== "rear" && (
          <Body testid="device-front">
            <div className={`flex h-full flex-col p-3 ${color && flash ? "alert-flash" : ""}`} style={{ background: color ? COLORS[color] : "#17191C", filter: `brightness(${0.5 + prefs.brightness / 200})` }}>
              <div className={`text-[9px] font-bold tracking-[0.3em] ${color === "yellow" ? "text-[#17191C]" : "text-white/70"}`}>TRO · REFEREE ONE</div>
              {color ? (
                <div className={`flex flex-1 flex-col items-center justify-center text-center ${color === "yellow" ? "text-[#17191C]" : "text-white"}`}>
                  <div className={`${small ? "text-base" : "text-2xl"} font-extrabold uppercase`}>{color} card</div>
                  {alert?.player && <>
                    <div className="my-2 overflow-hidden rounded-full border-2 border-white" style={{ width: small ? 44 : 70, height: small ? 44 : 70 }}>{imgUrl(alert.player.photo) && <img src={imgUrl(alert.player.photo)} alt="" className="h-full w-full object-cover" />}</div>
                    <div className="text-sm font-extrabold">#{alert.player.number} {alert.player.name}</div>
                    <div className="text-[10px] font-semibold opacity-80">{alert.player.club}</div>
                    <div className="mt-1 font-mono text-xs font-bold">{fmtClock((alert.minute || 0) * 60 + (alert.second || 0))}</div>
                  </>}
                </div>
              ) : <div className="flex-1 overflow-hidden pt-2 text-white">{frontNormal || <div className="mt-10 text-center text-xs text-white/60">Tactical match interface</div>}</div>}
            </div>
          </Body>
        )}
        {side !== "front" && (
          <Body testid="device-rear">
            <div className="relative h-full">
              <LedMatrix color={color} brightness={prefs.brightness} flash={flash} />
              <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded bg-[#17191C]/80 px-2 text-[10px] font-extrabold tracking-[0.3em] text-[#C5CBD1]">TRO</div>
            </div>
          </Body>
        )}
      </div>
      <div className="max-w-xs text-center text-[10px] text-[#6C757D]">SIMULATED in browser. Physical LEDs are only controlled through a supported hardware adapter.</div>
    </div>
  );
}
